import { Download } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { Button } from '../../components/ui'
import {
  detectInstallEnvironment,
  installOrigin,
  isLocalPreviewOrigin,
} from '../../domain/pwa'
import {
  isDeferredInstallPrompt,
  requestDeferredInstall,
  type DeferredInstallPrompt,
  type InstallChoice,
} from '../../services/pwaInstall'
import { InstallCaravanSheet } from './InstallCaravanSheet'
import { PwaContext, type PwaContextValue, usePwa } from './pwaContext'

function runtimeStandalone(): boolean {
  const withStandalone = navigator as Navigator & { standalone?: boolean }
  return withStandalone.standalone === true || window.matchMedia('(display-mode: standalone)').matches
}

export function PwaProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState<DeferredInstallPrompt | null>(null)
  const [installChoice, setInstallChoice] = useState<InstallChoice | null>(null)
  const [installed, setInstalled] = useState(runtimeStandalone)

  useEffect(() => {
    function captureInstallPrompt(event: Event): void {
      if (!isDeferredInstallPrompt(event)) return
      event.preventDefault()
      setPrompt(event)
      setInstallChoice(null)
    }
    function markInstalled(): void {
      setInstalled(true)
      setPrompt(null)
    }
    window.addEventListener('beforeinstallprompt', captureInstallPrompt)
    window.addEventListener('appinstalled', markInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt)
      window.removeEventListener('appinstalled', markInstalled)
    }
  }, [])

  const environment = detectInstallEnvironment({
    displayModeStandalone: installed || window.matchMedia('(display-mode: standalone)').matches,
    hasInstallPrompt: prompt !== null,
    maxTouchPoints: navigator.maxTouchPoints,
    navigatorStandalone: (navigator as Navigator & { standalone?: boolean }).standalone === true,
    userAgent: navigator.userAgent,
    vendor: navigator.vendor,
  })
  const origin = installOrigin(window.location.href)

  const installNow = useCallback(async () => {
    if (!prompt) return
    setPrompt(null)
    setInstallChoice(await requestDeferredInstall(prompt))
  }, [prompt])

  const value = useMemo<PwaContextValue>(() => ({
    closeInstaller: () => setOpen(false),
    environment,
    installChoice,
    installNow,
    isLocalOrigin: isLocalPreviewOrigin(origin),
    openInstaller: () => setOpen(true),
    origin,
  }), [environment, installChoice, installNow, origin])

  return (
    <PwaContext.Provider value={value}>
      {children}
      {open ? <InstallCaravanSheet /> : null}
    </PwaContext.Provider>
  )
}

export function InstallCaravanButton({ compact = false }: { compact?: boolean }) {
  const { openInstaller } = usePwa()
  return (
    <Button
      aria-label="Installer Caravan"
      className={compact ? 'install-entry install-entry--compact' : 'install-entry'}
      title={compact ? 'Installer Caravan' : undefined}
      variant="ghost"
      onClick={openInstaller}
    >
      <Download size={compact ? 18 : 17} aria-hidden="true" />
      {compact ? null : 'Installer Caravan'}
    </Button>
  )
}
